import { useContext, useEffect, useState } from 'react';
import {
  ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon,
  PlusIcon, PencilSquareIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { REACT_APP_UPLOADS } from '../../../constants';
import axiosInstance from '../../../utils/axios';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';
import RecordFormModal, { RecordData } from '../../../components/RecordFormModal';
import DataShield from '../../../components/DataShield';
import { maskName } from '../../../utils/privacy';
import MaskedName from '../../../components/MaskedName';

const thCls = 'text-left text-white/30 text-[9px] font-semibold uppercase tracking-wider pb-3 pr-5 whitespace-nowrap';
const tdCls = 'py-3 pr-5 border-t border-white/5 text-white/70 text-xs align-middle';

const PAGE_SIZE = 15;

function statusBadge(s: string) {
  const l = s?.toLowerCase();
  if (l === 'ongoing')   return 'bg-violet-500/15 text-violet-400 border-violet-500/20';
  if (l === 'concluded') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20';
  return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
}

/* ── Delete confirmation mini-modal ─────────────────────────────────── */
function DeleteConfirm({
  record, onConfirm, onCancel, deleting, role,
}: {
  record: RecordData;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
  role: string;
}) {
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}>
      <div className="rounded-2xl p-6 w-[380px] flex flex-col gap-4" style={{ background: "#061a0e", border: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(239,68,68,0.12)" }}>
            <TrashIcon className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <p className="text-white/85 text-sm font-semibold">Delete Record</p>
            <p className="text-white/35 text-xs mt-0.5">This action cannot be undone.</p>
          </div>
        </div>
        <p className="text-white/50 text-xs leading-relaxed" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "10px 12px" }}>
          <MaskedName name={record.nameOfAccused} role={role} className="text-white/80 font-semibold" />
          {record.species ? ` · ${record.species}` : ''}
          {record.dateOfSeizure ? ` · ${record.dateOfSeizure}` : ''}
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
            style={{ background: "rgba(239,68,68,0.75)", border: "1px solid rgba(239,68,68,0.4)", color: "#fff" }}
          >
            {deleting && (
              <svg width="12" height="12" viewBox="0 0 24 24" className="animate-spin">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            )}
            {deleting ? 'Deleting…' : 'Yes, delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main view ───────────────────────────────────────────────────────── */
export default function DatasetView() {
  const { state } = useContext(AuthContext);
  const role = state.userData?.role?.toLowerCase() ?? '';
  const isAdmin = role === 'admin' || role === 'super-admin';

  const [allData,      setAllData]      = useState<RecordData[]>([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState<number | null>(null);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  /* modal state */
  const [formMode,    setFormMode]    = useState<'add' | 'edit'>('add');
  const [formRecord,  setFormRecord]  = useState<RecordData | undefined>(undefined);
  const [showForm,    setShowForm]    = useState(false);

  /* delete state */
  const [deleteTarget, setDeleteTarget] = useState<RecordData | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Fetch ────────────────────────────────────────────────────────── */
  function fetchPage(p: number) {
    setLoading(true);
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/all?page=${p}&size=${PAGE_SIZE}`)
      .then((res) => {
        if (res.data?.content) {
          setAllData(res.data.content);
          setTotal(res.data.totalElements ?? res.data.content.length);
        } else if (Array.isArray(res.data)) {
          setAllData(res.data);
          setTotal(res.data.length);
        }
      })
      .catch(() => {
        axios.get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/all-cases`)
          .then((res) => { if (Array.isArray(res.data)) { setAllData(res.data); setTotal(res.data.length); } })
          .catch(() => { /* no data */ });
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchPage(page); }, [page]);

  /* ── Handlers ────────────────────────────────────────────────────── */
  function openAdd() {
    setFormMode('add');
    setFormRecord(undefined);
    setShowForm(true);
  }

  function openEdit(record: RecordData, e: React.MouseEvent) {
    e.stopPropagation();
    setFormMode('edit');
    setFormRecord(record);
    setShowForm(true);
  }

  function handleSaved(saved: RecordData) {
    setShowForm(false);
    // Optimistically update local list
    if (formMode === 'add') {
      setAllData((prev) => [saved, ...prev]);
      setTotal((t) => t + 1);
    } else {
      setAllData((prev) => prev.map((r) => r.id === saved.id ? saved : r));
    }
    setExpanded(null);
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/v1/seizure-cases/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${state.userToken}` },
      });
      setAllData((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setTotal((t) => Math.max(0, t - 1));
      setExpanded(null);
    } catch {
      /* silently fail — could show toast here */
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  /* ── Filter ─────────────────────────────────────────────────────── */
  const filtered = allData.filter((item) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      item.species?.toLowerCase().includes(q) ||
      item.location?.toLowerCase().includes(q) ||
      item.nameOfAccused?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' ||
      item.caseStatus?.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <>
      <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Dataset Management
            </h2>
            <p className="text-white/30 text-xs mt-0.5">{total} total records</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
              <input
                className="bg-white/5 border border-white/10 text-white text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 w-52"
                placeholder="Search species, location, accused..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="bg-[#0a2e18] border border-white/10 text-white text-xs rounded-lg px-3 py-2 focus:outline-none appearance-none cursor-pointer"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Concluded">Concluded</option>
              <option value="Absconded">Absconded</option>
            </select>
            {isAdmin && (
              <button
                onClick={openAdd}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all"
              >
                <PlusIcon className="w-3.5 h-3.5" /> Add Record
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 rounded-full border-2 border-green-500/30 border-t-green-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-white/25 text-sm">No records match your search</p>
          </div>
        ) : (
          <DataShield active={!isAdmin}>
          <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.15)' }}>
                  <th className={`${thCls} pl-5 w-10`}>#</th>
                  <th className={thCls}>Species</th>
                  <th className={thCls}>Location</th>
                  <th className={thCls}>Accused</th>
                  <th className={thCls}>Date</th>
                  <th className={thCls}>Status</th>
                  <th className={`${thCls} pr-5 w-8`} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => (
                  <>
                    <tr
                      key={idx}
                      className="hover:bg-white/3 cursor-pointer transition-colors"
                      onClick={() => setExpanded(expanded === idx ? null : idx)}
                    >
                      <td className={`${tdCls} pl-5 text-white/25 tabular-nums`}>{page * PAGE_SIZE + idx + 1}</td>
                      <td className={`${tdCls} font-semibold text-white/85`}>{item.species || '—'}</td>
                      <td className={tdCls}>{item.location || '—'}</td>
                      <td className={tdCls}><MaskedName name={item.nameOfAccused} role={role} /></td>
                      <td className={`${tdCls} text-white/40`}>{item.dateOfSeizure || '—'}</td>
                      <td className={tdCls}>
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusBadge(item.caseStatus ?? '')}`}>
                          {item.caseStatus || '—'}
                        </span>
                      </td>
                      <td className={`${tdCls} pr-5 text-right`}>
                        {expanded === idx
                          ? <ChevronUpIcon className="w-3.5 h-3.5 text-white/30 inline" />
                          : <ChevronDownIcon className="w-3.5 h-3.5 text-white/20 inline" />}
                      </td>
                    </tr>

                    {expanded === idx && (
                      <tr key={`${idx}-exp`}>
                        <td colSpan={7} className="pl-5 pr-5 pb-4 pt-0" style={{ background: 'rgba(0,0,0,0.12)' }}>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 py-4 border-t border-white/5">
                            {([
                              ['Nationality',   item.nationality],
                              ['Charge 1',      item.charge1],
                              ['Alt. Charge 1', item.alternativeCharge1],
                              ['Exhibit',       item.exhibit],
                              ['Weight',        item.weight],
                              ['Judgment Date', item.judgmentDate],
                              ['Outcome',       item.judgmentOutcome],
                              ['Sentencing',    item.sentencing],
                              ['Coordinates',   item.latitude != null
                                ? `${item.latitude}, ${item.longitude}`
                                : 'Missing ⚠'],
                            ] as [string, string | undefined | null][]).map(([k, v]) => (
                              <div key={k}>
                                <p className="text-white/22 text-[9px] uppercase tracking-wider mb-0.5">{k}</p>
                                <p className={`text-xs ${v === 'Missing ⚠' ? 'text-amber-400' : 'text-white/65'}`}>{v || '—'}</p>
                              </div>
                            ))}
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2 pt-2 border-t border-white/5">
                              <button
                                onClick={(e) => openEdit(item, e)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-700/25 text-sky-400 border border-sky-500/20 text-xs hover:bg-sky-700/40 transition-all"
                              >
                                <PencilSquareIcon className="w-3.5 h-3.5" />
                                Edit Record
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-700/20 text-red-400 border border-red-500/20 text-xs hover:bg-red-700/35 transition-all"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
          </DataShield>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-white/25 text-xs tabular-nums">
              Page {page + 1} of {totalPages} &mdash; {total} records
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 rounded-lg border border-white/10 text-white/40 text-xs hover:bg-white/5 disabled:opacity-25 transition-all"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-white/10 text-white/40 text-xs hover:bg-white/5 disabled:opacity-25 transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Record form modal (Add / Edit) ─────────────────────────── */}
      {showForm && (
        <RecordFormModal
          mode={formMode}
          initial={formRecord}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
        />
      )}

      {/* ── Delete confirmation ─────────────────────────────────────── */}
      {deleteTarget && (
        <DeleteConfirm
          record={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
          role={role}
        />
      )}
    </>
  );
}
